import React, { useEffect, useState } from "react";
import {
	Container,
	Button,
	Card,
	Form,
	FormControl,
	Col,
	Row,
	Table,
	Offcanvas,
	Stack,
} from "react-bootstrap";
import { getSeries, getSports } from "../../services/series";
import {
	createEvent,
	deleteEvent,
	getEvent,
	getEvents,
	updateEvent,
	uploadImage,
} from "../../services/eventService";
import { getEventAnalytics } from "../../services/resultService";
import { getSeasons } from "../../services/seasonService";
import {
	buildEventDate,
	formatEventDateTime,
	getEventDateParts,
} from "../../utils/eventDateTime";
import AdminLayout from "./AdminLayout";

const AdminEventsPage = () => {
	const [eventName, setEventName] = useState("");
	const [eventList, setEventList] = useState([]);
	const [date, setDate] = useState("");
	const [time, setTime] = useState("00:00");
	const [location, setLocation] = useState("");
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [seriesDropdown, setSeriesDropdown] = useState([]);
	const [selectedSeries, setSelectedSeries] = useState("");
	const [sportDropdown, setSportDropdown] = useState([]);
	const [selectedSport, setSelectedSport] = useState("");
	const [description, setDescription] = useState("");
	const [country, setCountry] = useState("");
	const [image, setImage] = useState(null);
	const [trackImage, setTrackImage] = useState(null);
	const [eventUrl, setEventUrl] = useState("");
	const [analytics, setAnalytics] = useState([]);
	const [seasons, setSeasons] = useState([]);
	const [selectedSeason, setSelectedSeason] = useState("");
	const [filterSport, setFilterSport] = useState("");
	const [filterSeries, setFilterSeries] = useState("");
	const [filterSeason, setFilterSeason] = useState("");
	const [showDrawer, setShowDrawer] = useState(false);

	const isFutureEvent = (event) => {
		const eventDate = new Date(event?.date);
		return !Number.isNaN(eventDate.getTime()) && eventDate > new Date();
	};

	useEffect(() => {
		getAllSeries();
		getAllSports();
		getAllEvents();
		getAllSeasons();
	}, []);

	const getAllSeries = async () => {
		try {
			const res = await getSeries();
			if (res) {
				setSeriesDropdown(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getAllSports = async () => {
		try {
			const res = await getSports();
			if (res) {
				setSportDropdown(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getAllEvents = async () => {
		try {
			const res = await getEvents();
			if (res) {
				setEventList(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getAllSeasons = async () => {
		try {
			const res = await getSeasons();
			if (res) {
				console.log(res);
				setSeasons(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getAnalytics = async () => {
		try {
			const res = await getEventAnalytics(selectedEvent._id);
			if (res) {
				setAnalytics(res.data);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleImageChange = (e) => {
		setImage(e.target.files[0]);
	};

	const refreshFields = () => {
		setEventName("");
		setSelectedSport("");
		setDate("");
		setTime("00:00");
		setLocation("");
		setSelectedSeries("");
		setDescription("");
		setCountry("");
		setEventUrl("");
		setSelectedSeason("");
		setSelectedEvent(null);
	};

	const handleSelectEvent = async (event) => {
		const eventDateParts = getEventDateParts(event.date);
		setSelectedEvent(event);
		setCountry(event.country);
		setDate(eventDateParts.date);
		setTime(eventDateParts.time);
		setDescription(event.description);
		setEventName(event.name);
		setLocation(event.location);
		setSelectedSeries(event.series?._id ?? event.series ?? "");
		setSelectedSport(event.sport?._id ?? event.sport ?? "");
		setEventName(event.name);
		setSelectedSeason(event.season?._id ?? event.season ?? "");
	};

	const openCreateDrawer = () => {
		refreshFields();
		setShowDrawer(true);
	};

	const openEditDrawer = (event) => {
		handleSelectEvent(event);
		setShowDrawer(true);
	};

	const createNewEvent = async () => {
		try {
			const res = await createEvent({
				name: eventName,
				sport: selectedSport,
				date: buildEventDate(date, time || "00:00"),
				location,
				series: selectedSeries,
				country: country,
				description,
				eventUrl,
				season: selectedSeason,
			});
			if (res) {
				refreshFields();
				getAllEvents();
				setShowDrawer(false);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const updateEventAsync = async () => {
		try {
			const res = await updateEvent(selectedEvent._id, {
				name: eventName,
				sport: selectedSport,
				date: buildEventDate(date, time || "00:00"),
				location,
				series: selectedSeries,
				country: country,
				description,
				eventUrl,
				season: selectedSeason,
			});
			if (res) {
				console.log(res);
				refreshFields();
				getAllEvents();
				setShowDrawer(false);
			}
		} catch (error) {
			console.log(error);
			alert("Error updating event");
		}
	};

	const deleteEventAsync = async (event) => {
		if (!event?._id || !isFutureEvent(event)) {
			alert("Only future events can be deleted.");
			return;
		}

		const confirmed = window.confirm(
			`Delete ${event.name}? This cannot be undone.`,
		);
		if (!confirmed) {
			return;
		}

		try {
			const res = await deleteEvent(event._id);
			if (res) {
				if (selectedEvent?._id === event._id) {
					refreshFields();
					setShowDrawer(false);
				}
				getAllEvents();
			}
		} catch (error) {
			console.error("Error deleting event:", error);
			const message =
				typeof error?.response?.data === "string"
					? error.response.data
					: "Error deleting event";
			alert(message);
		}
	};

	const handleSubmitImage = async (e) => {
		e.preventDefault();
		try {
			const res = await uploadImage(selectedEvent._id, image);
			if (res) {
				console.log(res);
				// setImage(null);
			}
		} catch (error) {
			alert("Error uploading image");
		}
	};

	const filteredEvents = eventList.filter((event) => {
		const eventSportId = event?.sport?._id ?? event?.sport ?? "";
		const eventSeriesId = event?.series?._id ?? event?.series ?? "";
		const eventSeasonId = event?.season?._id ?? event?.season ?? "";

		if (filterSport && eventSportId !== filterSport) {
			return false;
		}
		if (filterSeries && eventSeriesId !== filterSeries) {
			return false;
		}
		if (filterSeason && eventSeasonId !== filterSeason) {
			return false;
		}
		return true;
	});

	return (
		<AdminLayout>
			<Container className="py-3 px-4">
				{/* Show All Current events */}
				<Card className="mt-4 p-4 shadow border-0">
					<Card.Title className="fw-bold mb-3 ms-4">Current Events</Card.Title>
					<Row className="g-3 align-items-end mb-3">
						<Col md={3}>
							<Form.Group>
								<Form.Label>Filter by Sport</Form.Label>
								<Form.Select
									value={filterSport}
									onChange={(e) => setFilterSport(e.target.value)}
								>
									<option value="">All Sports</option>
									{sportDropdown.map((sportOption) => (
										<option key={sportOption._id} value={sportOption._id}>
											{sportOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
						</Col>
						<Col md={3}>
							<Form.Group>
								<Form.Label>Filter by Series</Form.Label>
								<Form.Select
									value={filterSeries}
									onChange={(e) => setFilterSeries(e.target.value)}
								>
									<option value="">All Series</option>
									{seriesDropdown.map((seriesOption) => (
										<option key={seriesOption._id} value={seriesOption._id}>
											{seriesOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
						</Col>
						<Col md={3}>
							<Form.Group>
								<Form.Label>Filter by Season</Form.Label>
								<Form.Select
									value={filterSeason}
									onChange={(e) => setFilterSeason(e.target.value)}
								>
									<option value="">All Seasons</option>
									{seasons.map((seasonOption) => (
										<option key={seasonOption._id} value={seasonOption._id}>
											{seasonOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
						</Col>
						<Col md={3} className="d-flex justify-content-end">
							<Button variant="primary" onClick={openCreateDrawer}>
								Create Event
							</Button>
						</Col>
					</Row>
					<Card.Body
						style={{ maxHeight: "30rem", overflow: "auto", fontSize: "0.8rem" }}
					>
						<Table
							className=""
							striped
							bordered
							hover
							// responsive
						>
							<thead>
								<tr>
									<th>Name</th>
									<th>Sport</th>
									<th>Series</th>
									<th>Date</th>
									<th>Location</th>
									<th>Country</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredEvents.map((event) => (
									<tr key={event._id}>
										<td>{event.name}</td>
										<td>{event.sport.name}</td>
										<td>{event.series.name}</td>
										<td>{formatEventDateTime(event.date)}</td>
										<td>{event.location}</td>
										<td>{event.country}</td>
										<td>
											<Stack direction="horizontal" gap={2}>
												<Button
													variant="outline-primary"
													size={"sm"}
													onClick={() => openEditDrawer(event)}
												>
													Edit
												</Button>
												<Button
													variant="outline-danger"
													size={"sm"}
													onClick={() => deleteEventAsync(event)}
													disabled={!isFutureEvent(event)}
													title={
														isFutureEvent(event)
															? "Delete event"
															: "Only future events can be deleted"
													}
												>
													Delete
												</Button>
											</Stack>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Card.Body>
				</Card>
				<div className="d-flex flex-row justify-content-center">
					<Button
						variant="outline-primary"
						className="mt-3"
						onClick={getAnalytics}
					>
						Get Analytics
					</Button>
				</div>

				<Offcanvas
					show={showDrawer}
					onHide={() => setShowDrawer(false)}
					placement="end"
					className="admin-offcanvas"
				>
					<Offcanvas.Header closeButton>
						<Offcanvas.Title>
							{selectedEvent?._id ? "Edit Event" : "Create Event"}
						</Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<Form>
							<Form.Group controlId="eventName" className="mb-3">
								<Form.Label>Event Name</Form.Label>
								<FormControl
									type="text"
									placeholder="Enter event name"
									value={eventName}
									onChange={(e) => setEventName(e.target.value)}
								/>
							</Form.Group>
							<Form.Group controlId="eventSport" className="mb-3">
								<Form.Label>Sport</Form.Label>
								<Form.Select
									value={selectedSport}
									onChange={(e) => setSelectedSport(e.target.value)}
								>
									<option value="">Select a sport</option>
									{sportDropdown.map((sportOption) => (
										<option key={sportOption._id} value={sportOption._id}>
											{sportOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Row className="mb-3">
								<Col sm={7}>
									<Form.Group controlId="eventDate">
										<Form.Label>Date</Form.Label>
										<FormControl
											type="date"
											placeholder="Enter date"
											value={date}
											onChange={(e) => setDate(e.target.value)}
										/>
									</Form.Group>
								</Col>
								<Col sm={5}>
									<Form.Group controlId="eventTime">
										<Form.Label>Time (Eastern)</Form.Label>
										<FormControl
											type="time"
											value={time}
											onChange={(e) => setTime(e.target.value)}
										/>
									</Form.Group>
								</Col>
							</Row>
							<Form.Group controlId="eventLocation" className="mb-3">
								<Form.Label>Location</Form.Label>
								<FormControl
									type="text"
									placeholder="Enter location"
									value={location}
									onChange={(e) => setLocation(e.target.value)}
								/>
							</Form.Group>
							<Form.Group controlId="eventSeries" className="mb-3">
								<Form.Label>Series</Form.Label>
								<Form.Select
									value={selectedSeries}
									onChange={(e) => setSelectedSeries(e.target.value)}
								>
									<option value="">Select a series</option>
									{seriesDropdown.map((seriesOption) => (
										<option key={seriesOption._id} value={seriesOption._id}>
											{seriesOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Form.Group controlId="eventCountry" className="mb-3">
								<Form.Label>Country</Form.Label>
								<FormControl
									type="text"
									placeholder="Enter country"
									value={country}
									onChange={(e) => setCountry(e.target.value)}
								/>
							</Form.Group>
							<Form.Group controlId="eventDescription" className="mb-3">
								<Form.Label>Description</Form.Label>
								<FormControl
									as="textarea"
									placeholder="Enter description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
								/>
							</Form.Group>
							<Form.Group controlId="eventSeason" className="mb-3">
								<Form.Label>Season</Form.Label>
								<Form.Select
									value={selectedSeason}
									onChange={(e) => setSelectedSeason(e.target.value)}
								>
									<option value="">Select a season</option>
									{seasons.map((seasonOption) => (
										<option key={seasonOption._id} value={seasonOption._id}>
											{seasonOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Form.Group controlId="eventUrl" className="mb-3">
								<Form.Label>Event Site URL</Form.Label>
								<FormControl
									type="text"
									placeholder="Enter event site url"
									value={eventUrl}
									onChange={(e) => setEventUrl(e.target.value)}
								/>
							</Form.Group>

							{selectedEvent && (
								<Form className="text-end mb-2">
									<Form.Group controlId="eventImage" className="mb-3">
										<Form.Label>Main Image</Form.Label>
										<Form.Control type="file" onChange={handleImageChange} />
									</Form.Group>
									<Button
										variant="secondary"
										size={"sm"}
										onClick={handleSubmitImage}
									>
										Upload Image
									</Button>
								</Form>
							)}
						</Form>
						<Stack direction="horizontal" gap={2} className="justify-content-end">
							<Button variant="outline-secondary" onClick={refreshFields}>
								Reset
							</Button>
							{selectedEvent?._id ? (
								<Button variant="primary" onClick={updateEventAsync}>
									Update Event
								</Button>
							) : (
								<Button variant="primary" onClick={createNewEvent}>
									Create Event
								</Button>
							)}
						</Stack>
					</Offcanvas.Body>
				</Offcanvas>
			</Container>
		</AdminLayout>
	);
};

export default AdminEventsPage;
